/**
 * Legacy URL compatibility.
 *
 * Two generations of bookmarks point at this console:
 *
 *  1. The pre-T4.1 SvelteKit admin, which itself 308'd `/receiving/*` and
 *     `/vendors/purchase-orders/*` into `/purchasing/*` from `hooks.server.ts`.
 *     That layer died with the old app; the rules live on here.
 *  2. The screens this console consolidated. `/inventory/lots` became
 *     `/inventory?tab=lots`, the five reports became `?report=`, and the
 *     catalog's reference data (departments, brands, zones, barcodes, labels)
 *     collapsed into `/organize?tab=`.
 *
 * An operator with a months-old bookmark must land on the screen they meant,
 * not on a 404 and — worse — not on a half-rendered detail screen showing a
 * validator error, which is what `/products/departments` did before this
 * existed (it matched `/products/$id` with `id="departments"`).
 *
 * RETIREMENT
 * ----------
 * The old admin gave its legacy set three months past cutover. This table gets
 * the same: it may be deleted on or after **2026-11-14**, along with
 * `src/routes/$.tsx`, the `beforeLoad` hook in `src/routes/__root.tsx` and this
 * module's test. Nothing else imports it.
 */

export interface LegacyRedirect {
  /** The dead path, without query string and without a trailing slash. */
  from: string;
  /** Canonical destination. May carry a query string of its own. */
  to: string;
  /**
   * When true, `from` also matches every path beneath it and the remaining
   * segments are carried over: `/receiving/12` → `/purchasing/12`. Exact
   * entries are always tried first, so a prefix rule never steals a path that
   * has its own row.
   */
  prefix?: true;
  /** Why this row exists — read by whoever eventually deletes the table. */
  note?: string;
}

/**
 * One table, evaluated exact-first then prefix. Grouped by the reorg that
 * killed each URL rather than alphabetically, because that is the unit in
 * which rows will eventually be retired.
 */
export const LEGACY_REDIRECTS: readonly LegacyRedirect[] = [
  /* ── Purchasing: inherited from the old admin's own 308 table ──────────── */
  {
    from: '/vendors/purchase-orders/new',
    to: '/purchasing/new',
    note: 'Exact rows must precede the /vendors/purchase-orders prefix rule.',
  },
  { from: '/vendors/purchase-orders/import-invoice', to: '/purchasing/import' },
  { from: '/vendors/purchase-orders/quick-receive', to: '/purchasing/receive' },
  {
    from: '/vendors/purchase-orders/product-search',
    to: '/purchasing',
    note: 'Was a +server.ts JSON endpoint, never a screen. Product search now lives inside the PO editor.',
  },
  {
    from: '/purchasing/product-search',
    to: '/purchasing',
    note: 'Same endpoint under its post-reorg path. Without this row it matches /purchasing/$id.',
  },
  {
    from: '/orders/new/product-search',
    to: '/orders/new',
    note: 'Ditto, for the order builder.',
  },
  {
    from: '/receiving',
    to: '/purchasing',
    prefix: true,
    note: 'Segment-aware: /receiving-notes is left alone.',
  },
  { from: '/vendors/purchase-orders', to: '/purchasing', prefix: true },

  /* ── Inventory: seven sibling pages → one screen with ?tab= ────────────── */
  { from: '/inventory/low-stock', to: '/inventory?tab=low' },
  { from: '/inventory/expiring', to: '/inventory?tab=expiring' },
  { from: '/inventory/lots', to: '/inventory?tab=lots' },
  { from: '/inventory/tracking', to: '/inventory?tab=tracking' },
  // TASK-0259: the prototype tab is retired; Reports › Fresh in / out replaced it.
  { from: '/inventory/fresh-production', to: '/reports?report=fresh-in-out' },
  { from: '/inventory/import', to: '/inventory?tab=import' },

  /* ── Reports: five sibling pages → one screen with ?report= ────────────── */
  { from: '/reports/sales', to: '/reports?report=sales' },
  { from: '/reports/products', to: '/reports?report=products' },
  { from: '/reports/inventory', to: '/reports?report=inventory' },
  { from: '/reports/financial', to: '/reports?report=financial' },
  { from: '/reports/cashiers', to: '/reports?report=cashiers' },

  /* ── Catalog reference data → Organize ─────────────────────────────────── */
  {
    from: '/products/departments',
    to: '/organize?tab=departments',
    note: 'These four are the urgent ones: they match /products/$id and rendered a raw Zod message.',
  },
  { from: '/products/brands', to: '/organize?tab=brands' },
  { from: '/products/barcodes', to: '/organize?tab=barcodes' },
  { from: '/products/labels', to: '/organize?tab=labels' },
  { from: '/system/zones', to: '/organize?tab=zones' },

  /* ── Pricing ───────────────────────────────────────────────────────────── */
  { from: '/pricing/promotions', to: '/pricing?tab=promotions' },

  /* ── Renamed top-level screens ─────────────────────────────────────────── */
  { from: '/cash-management', to: '/cash' },
  {
    from: '/sync-quarantine',
    to: '/system/sync',
    note: 'Quarantine is now one panel of the Sync health screen.',
  },
];

/** Trailing slashes are noise in a bookmark; `/receiving/` is `/receiving`. */
function normalize(pathname: string): string {
  if (pathname.length <= 1) return pathname;
  return pathname.replace(/\/+$/, '') || '/';
}

/**
 * Merge the bookmark's query string into the destination's.
 *
 * The destination's own params win — an operator arriving from
 * `/inventory/lots?tab=stock` asked for lots by *path*, and the path is the
 * more specific statement of intent. Everything else rides along, so
 * `/reports/sales?from=2026-01-01` keeps its date range.
 */
function withQuery(to: string, search: string): string {
  const incomingRaw = search.startsWith('?') ? search.slice(1) : search;
  if (!incomingRaw) return to;

  const [path = to, destQuery = ''] = to.split('?');
  const merged = new URLSearchParams(destQuery);
  // Snapshot the destination's keys up front: a repeated incoming key
  // (`?dept=1&dept=2`) must survive intact, and testing `merged.has()` as we go
  // would drop everything after the first.
  const claimed = new Set(merged.keys());
  for (const [key, value] of new URLSearchParams(incomingRaw)) {
    if (!claimed.has(key)) merged.append(key, value);
  }
  const query = merged.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Resolve a legacy path to its canonical destination, or `null` if the path is
 * not a retired URL (either it is current, or it never existed).
 *
 * Pure and side-effect free so it can be unit tested and called from any
 * `beforeLoad` without a router instance.
 */
export function resolveLegacyPath(pathname: string, search = ''): string | null {
  const path = normalize(pathname);

  for (const rule of LEGACY_REDIRECTS) {
    if (!rule.prefix && rule.from === path) return withQuery(rule.to, search);
  }
  for (const rule of LEGACY_REDIRECTS) {
    if (!rule.prefix) continue;
    if (path === rule.from) return withQuery(rule.to, search);
    if (path.startsWith(`${rule.from}/`)) {
      return withQuery(rule.to + path.slice(rule.from.length), search);
    }
  }
  return null;
}
